import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { IClientShippingUI } from 'v2/modules/client/interfaces/client-shipping.interface';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IClientInterface } from 'v2/modules/shared/interfaces/client.interface';
import { OptionInterface } from 'v2/modules/shared/interfaces/common.interface';
import { ClientApiService } from 'v2/modules/shared/services/api/client/client-api.service';
import { ClientShippingApiService } from 'v2/modules/shared/services/api/client/client-shipping-api.service';

@Component({
  selector: 'lum-client-profile-edit-shipping-address-form',
  templateUrl: './client-profile-edit-shipping-address-form.component.html',
  styleUrls: ['./client-profile-edit-shipping-address-form.component.scss'],
})
export class ClientProfileEditShippingAddressFormComponent implements OnInit {
  @Input()
  form :UntypedFormGroup;

  @Output()
  tabChange = new EventEmitter<any>();

  shippingAddress$ :Observable<OptionInterface[]>;
  loading$ :Observable<boolean>;

  addAddressToggle :boolean = false;
  shippingAddresses :IClientShippingUI[] = [];

  private onDestroy$ = new Subject();

  constructor(
    private snackBar :MatSnackBar,
    private clientShippingApiService :ClientShippingApiService,
    private clientApiService :ClientApiService,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.fetchClientShippingAddressesFromService();
    this.getShippingAddressFromObservable().subscribe();
  }
  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onChangeTab() :void {
    this.tabChange.emit({
      name: 'business-address',
      index: 1,
    });
  }
  onSetDefault(shippingAddress :IClientShippingUI) :void {
    if (shippingAddress && shippingAddress.id) {
      this.clientShippingApiService
        .setDefault(this.form.get('business_account_id')?.value, shippingAddress.id)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response) => {
          const shippingAddresses :IClientShippingUI[] = this.shippingAddresses;

          shippingAddresses.map(
            (address :IClientShippingUI) =>
              (address.default = address.id == shippingAddress.id),
          );

          this.clientShippingApiService.setShippingAddresses(shippingAddresses);

          this.snackBar.open('Successfully set as default address.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        });
    }
  }
  onDelete(shippingAddress :IClientShippingUI) :void {
    if (confirm('Are you sure to delete this shipping address?')) {
      if (shippingAddress && shippingAddress.id) {
        this.clientShippingApiService
          .delete(this.form.get('business_account_id')?.value, shippingAddress.id)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            const shippingAddresses :IClientShippingUI[] = this.shippingAddresses;

            const index :number = shippingAddresses.findIndex(
              (address :IClientShippingUI) => address.id == shippingAddress.id,
            );
            if (index >= 0) {
              shippingAddresses.splice(index, 1);
              this.clientShippingApiService.setShippingAddresses(shippingAddresses);
              this.snackBar.open('Address successfully deleted', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            }
          });
      }
    }
  }
  onSaveChanges() :void {
    this.clientApiService
      .updateProfile(this.form.get('business_account_id')?.value, this.form.value)
      .pipe(
        tap({
          next: (response) => {
            this.snackBar.open(response.message, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  private getDataFromService() :void {
    this.shippingAddress$ = this.clientShippingApiService.addresses$;
    this.loading$ = this.clientApiService.loading$;
  }
  private fetchClientShippingAddressesFromService() :void {
    this.clientShippingApiService.setShippingAddresses([]);
    this.clientShippingApiService.get({
      businessAccountId: this.form.get('business_account_id')?.value,
    });
  }
  private getShippingAddressFromObservable() :Observable<any> {
    return this.shippingAddress$.pipe(
      tap((addresses :any) => {
        if (addresses.length > 0) {
          this.shippingAddresses = addresses.map((address :IClientShippingUI) => ({
            ...address,
            toggle: false,
            fullName: this.getFullName(address),
            addressText: this.getAddressText(address),
          }));
        } else {
          this.shippingAddresses = [];
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
  private getFullName(shipping :IClientShippingUI) :string {
    return `${shipping.firstName} ${shipping.lastName}`;
  }
  private getAddressText(shipping :IClientShippingUI) :string {
    return `${shipping.address}, ${
      shipping.addressLine2 ? shipping.addressLine2 : ''
    }, ${shipping.city}, ${shipping.state} ${shipping.postalCode} ${
      shipping.country
    }`;
  }

  get getClient() :IClientInterface {
    return this.form.value;
  }
  get hasInvalidInput() :boolean {
    return (
      this.form?.controls['fein']?.invalid ||
      this.form?.controls['business_name']?.invalid ||
      this.form?.controls['business_address']?.invalid ||
      this.form?.controls['business_state']?.invalid ||
      this.form?.controls['business_city']?.invalid ||
      this.form?.controls['business_zip']?.invalid ||
      this.form?.controls['business_country']?.invalid ||
      this.form?.controls['phone']?.invalid
    );
  }
}
